import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    TextField,
    Autocomplete,
} from '@mui/material';
import { getFilter } from '../services/filterPanel';
import { getRegionCities } from '../services/regionApi';

const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const reorderRegionAfterCity = (filters) => {
    const cityIndex = filters.findIndex((f) => f.key === 'city');
    const regionIndex = filters.findIndex((f) => f.key === 'region');
    if (cityIndex === -1 || regionIndex === -1 || regionIndex === cityIndex + 1) return filters;

    const newFilters = [...filters];
    const [regionFilter] = newFilters.splice(regionIndex, 1);
    newFilters.splice(cityIndex + 1, 0, regionFilter);
    return newFilters;
};

export function FilterPanel({
    filters,
    setFilters,
    loading,
    onApplyFilters,
    onClearAll,
    selectedLocation,
    extraFields,
    excludeKeys = [],
    showApplyButton = true  
}) {
    const [filterConfig, setFilterConfig] = useState([]);
    const [regionOptions, setRegionOptions] = useState([]);

    useEffect(() => {
        async function fetchFiltersAndRegions(city) {
            if (!city) return;
            try {
                const [baseFiltersRes, regionRes] = await Promise.all([
                    getFilter(),
                    getRegionCities({ city }),
                ]);

                setRegionOptions(regionRes);

                const baseFilters = baseFiltersRes
                    .filter((item) => item.key !== 'city' && item.key !== 'region' && !excludeKeys.includes(item.key))
                    .map((item) => ({
                        key: item.key,
                        label: item.key.charAt(0).toUpperCase() + item.key.slice(1),
                        options: item.values,
                    }));

                const regionFilter = {
                    key: 'region',
                    label: 'Region',
                    options: regionRes.map((r) => r.regionName),
                    _fullRegionData: regionRes
                };

                const allFilters = reorderRegionAfterCity([...baseFilters, regionFilter]);
                setFilterConfig(allFilters);

                setFilters((prev) => {
                    const currentRegion = prev.region?.[0];
                    const stillValid = regionRes.find(r => r.regionName === currentRegion?.regionName);
                    return {
                        ...prev,
                        region: stillValid ? [currentRegion] : []
                    };
                });

            } catch (error) {
                console.error('Failed to fetch filters or region data:', error);
            }
        }

        fetchFiltersAndRegions(selectedLocation);
    }, [selectedLocation]);

    const findRegionIdByName = (name) => {
        const found = regionOptions.find((r) => r.regionName === name);
        return found ? found.regionId : null;
    };

    const handleChange = (categoryKey, value) => {
        if (categoryKey === 'region') {
            const regionId = findRegionIdByName(value);
            setFilters((prev) => ({
                ...prev,
                region: value ? [{ regionName: value, regionId }] : [],
            }));
        } else {
            setFilters((prev) => ({
                ...prev,
                [categoryKey]: value ? [value] : [],
            }));
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                px: 2,
                py: 2,
                backgroundColor: '#f9f9f9',
                borderBottom: '1px solid #ddd',
            }}
        >
            {filterConfig.map((category) => (
                <FormControl key={category.key} sx={{ minWidth: 250 }} size="small">
                    <Autocomplete
                        options={category.options}
                        value={
                            category.key === 'region'
                                ? filters.region?.[0]?.regionName || null
                                : filters[category.key]?.[0] || null
                        }
                        onChange={(event, newValue) => handleChange(category.key, newValue || '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={category.label}
                                variant="outlined"
                                size="small"
                            />
                        )}
                        disableClearable={false}
                        clearOnEscape
                    />
                </FormControl>
            ))}

            {extraFields}

            {showApplyButton && (
                <Button
                    variant="contained"
                    onClick={onApplyFilters}
                    disabled={loading}
                    sx={{
                        backgroundColor: '#70B92F',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: '#4e7e1e' }
                    }}
                >
                    {loading ? 'Applying...' : 'Apply'}
                </Button>
            )}

            <Button
                variant="contained"
                onClick={onClearAll}
                sx={{
                    backgroundColor: '#d94b3b',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: '#b13a2a' }
                }}
            >
                Reset
            </Button>
        </Box>
    );
}